/**
 * Settings → Prices tab. Manages mttr.vendor_customer_price rows.
 *
 *   Platform admins → list / set / end for any (vendor, customer, item).
 *   Vendor admins / Tech → list / set / end for their own vendor; the
 *                          vendor picker is locked.
 *   Customer admins → read-only on rows where customer_company_id == own.
 *
 * UX:
 *   - Filter row: Vendor (platform only), Customer, optional Item search.
 *   - Table below shows current effective prices for the filtered scope.
 *   - "Add / update price" form lets the operator pick an item, type a
 *     unit price + currency, and click Save (POST -> a fresh
 *     effective_utc row that supersedes any prior price for that triple).
 *   - "End" button per row soft-deletes the active price.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Modal from "../Modal";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { fmtUtc } from "../../lib/format";
import { fetchCompanies, type Company } from "../../services/adminApi";
import {
  type PriceImportResult,
  type PriceRow,
  downloadBlob,
  endPrice,
  exportPricesCsv,
  importPricesCsv,
  listPrices,
  upsertPrice,
} from "../../services/pricesApi";
import { searchItems, type ItemHit } from "../../services/tagApi";

const PAGE_SIZE = 100;

export const PricesTab: React.FC = () => {
  const { token } = useAuth();
  // This tab is platform-only (Settings hides it from
  // customer/vendor; the backend 403s anyway). No role-lock
  // branches needed.
  const [companies, setCompanies] = useState<Company[]>([]);
  const [vendorId, setVendorId] = useState<number | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);

  const [rows, setRows] = useState<PriceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [canWrite, setCanWrite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Add price" form state.
  const [itemQuery, setItemQuery] = useState("");
  const [itemHits, setItemHits] = useState<ItemHit[]>([]);
  const [pickedItem, setPickedItem] = useState<ItemHit | null>(null);
  const [unitPrice, setUnitPrice] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [custPart, setCustPart] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Bulk import/export state.
  const [importOpen, setImportOpen] = useState(false);
  const [importCsv, setImportCsv] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<PriceImportResult | null>(null);
  const [exportBusy, setExportBusy] = useState(false);

  // Load company list for the vendor + customer pickers.
  useEffect(() => {
    if (!token) return;
    fetchCompanies(token)
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, [token]);

  const vendors = useMemo(
    () => companies.filter((c) => c.company_type === "vendor"),
    [companies],
  );
  const customers = useMemo(
    () => companies.filter((c) => c.company_type === "customer"),
    [companies],
  );

  const load = useCallback(async () => {
    if (!token) return;
    // Don't auto-pull the whole ~1,200-row overlay on mount; require a
    // vendor or customer filter first (mirrors the Webhooks tab guard).
    if (vendorId == null && customerId == null) {
      setRows([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await listPrices(token, {
        vendor_id: vendorId ?? undefined,
        customer_id: customerId ?? undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(r.prices);
      setTotal(r.total);
      setCanWrite(r.can_write);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, vendorId, customerId, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  // Item search (debounced). A request-id guard ignores stale/out-of-order
  // responses so a slow earlier search can't overwrite newer results
  // (see ProductPicker for the pattern).
  const itemReqIdRef = useRef(0);
  useEffect(() => {
    if (!token || itemQuery.trim().length < 2) {
      setItemHits([]);
      return;
    }
    const id = ++itemReqIdRef.current;
    const handle = window.setTimeout(() => {
      searchItems(itemQuery.trim(), token)
        .then((hits) => { if (id === itemReqIdRef.current) setItemHits(hits); })
        .catch(() => { if (id === itemReqIdRef.current) setItemHits([]); });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [itemQuery, token]);

  const canSave =
    canWrite && pickedItem !== null && unitPrice.trim() !== "" &&
    !Number.isNaN(Number(unitPrice)) && Number(unitPrice) >= 0 &&
    vendorId != null && customerId != null;

  const onSave = async (): Promise<void> => {
    if (!token || !canSave || pickedItem == null || vendorId == null || customerId == null) return;
    setSaving(true);
    setError(null);
    try {
      await upsertPrice(token, {
        vendor_company_id: vendorId,
        customer_company_id: customerId,
        item_id: pickedItem.item_id,
        unit_price: Number(unitPrice),
        currency: currency.trim().toUpperCase() || "USD",
        note: note.trim() || undefined,
        customer_part_no: custPart.trim() || null,
      });
      setPickedItem(null);
      setItemQuery("");
      setUnitPrice("");
      setNote("");
      setCustPart("");
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const onEnd = async (r: PriceRow): Promise<void> => {
    if (!token || !canWrite) return;
    if (!window.confirm(`End the current price for ${r.sku ?? `#${r.item_id}`}? It will stop applying immediately.`)) return;
    setError(null);
    try {
      await endPrice(token, {
        vendor_company_id: r.vendor_company_id,
        customer_company_id: r.customer_company_id,
        item_id: r.item_id,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const fmtMoney = (n: number | null, ccy: string): string => {
    if (n == null) return "—";
    return new Intl.NumberFormat(undefined, { style: "currency", currency: ccy }).format(n);
  };

  // ---------- Bulk export / import handlers ----------

  const onExportCsv = async (): Promise<void> => {
    if (!token) return;
    setExportBusy(true);
    setError(null);
    try {
      const { blob, filename } = await exportPricesCsv(token, {
        vendor_id: vendorId ?? undefined,
        customer_id: customerId ?? undefined,
      });
      downloadBlob(blob, filename);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setExportBusy(false);
    }
  };

  const onPickImportFile = async (file: File): Promise<void> => {
    const text = await file.text();
    setImportCsv(text);
  };

  const onSubmitImport = async (): Promise<void> => {
    if (!token || !importCsv.trim()) return;
    setImporting(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await importPricesCsv(token, importCsv);
      setImportResult(result);
      // Refresh the table so the new rows show. Keep the modal open
      // so the user can see the per-row errors before dismissing.
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setImporting(false);
    }
  };

  const closeImport = (): void => {
    setImportOpen(false);
    setImportCsv("");
    setImportResult(null);
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs text-secondary-500">
          Per-(vendor, customer, item) price overrides. Each save creates a new
          effective row; the most recent wins. End a price to stop it without
          losing history. Customer part number is emitted as
          <code className="mx-1">custpart</code> on the weekly consignment
          report; vendor part number is <code className="mx-1">md_items.sku</code>.
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            size="sm" variant="outline"
            onClick={() => void onExportCsv()}
            disabled={exportBusy}
          >
            {exportBusy ? "Exporting…" : "Export CSV"}
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => setImportOpen(true)}
          >
            Import CSV
          </Button>
        </div>
      </div>

      {/* Picker row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-secondary-50 border border-secondary-200 rounded-md p-3">
        <label className="text-xs text-secondary-600">
          <div className="mb-1">Vendor</div>
          <select
            value={vendorId ?? ""}
            onChange={(e) => { setVendorId(e.target.value ? Number(e.target.value) : null); setOffset(0); }}
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="">— Any vendor —</option>
            {vendors.map((c) => (
              <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-secondary-600">
          <div className="mb-1">Customer</div>
          <select
            value={customerId ?? ""}
            onChange={(e) => { setCustomerId(e.target.value ? Number(e.target.value) : null); setOffset(0); }}
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm bg-white"
          >
            <option value="">— Any customer —</option>
            {customers.map((c) => (
              <option key={c.company_id} value={c.company_id}>{c.company_name}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Add / update form (writers only) */}
      {canWrite && (
        <div className="bg-secondary-50 border border-secondary-200 rounded-md p-3 space-y-2">
          <div className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">
            Add or update a price
          </div>
          {vendorId == null || customerId == null ? (
            <div className="text-xs text-secondary-500 italic">
              Pick a vendor and customer above to add a price.
            </div>
          ) : (
            <>
              <div className="relative">
                <label className="text-xs text-secondary-600">
                  <div>Item (SKU or name)</div>
                  <input
                    type="text"
                    value={pickedItem ? `${pickedItem.sku ?? `#${pickedItem.item_id}`} · ${pickedItem.name ?? ""}` : itemQuery}
                    onChange={(e) => {
                      setItemQuery(e.target.value);
                      setPickedItem(null);
                    }}
                    placeholder="Type 2+ chars…"
                    className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                  />
                </label>
                {!pickedItem && itemHits.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full bg-white border border-secondary-200 rounded-md shadow max-h-60 overflow-y-auto text-sm">
                    {itemHits.map((it) => (
                      <li key={it.item_id}>
                        <button
                          type="button"
                          onMouseDown={() => { setPickedItem(it); setItemHits([]); }}
                          className="w-full text-left px-3 py-1.5 hover:bg-secondary-50"
                        >
                          <span className="font-mono">{it.sku ?? `#${it.item_id}`}</span>
                          {it.name && <span className="ml-2 text-secondary-600">{it.name}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <label className="text-xs text-secondary-600">
                  <div>Unit price</div>
                  <input
                    type="number" min={0} step="0.0001"
                    value={unitPrice}
                    onChange={(e) => setUnitPrice(e.target.value)}
                    className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                  />
                </label>
                <label className="text-xs text-secondary-600">
                  <div>Currency</div>
                  <input
                    type="text" maxLength={8}
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="w-full border border-secondary-300 rounded px-2 py-1 text-sm uppercase"
                  />
                </label>
                <label className="text-xs text-secondary-600">
                  <div>Customer part #</div>
                  <input
                    type="text" maxLength={60}
                    value={custPart}
                    onChange={(e) => setCustPart(e.target.value)}
                    placeholder="e.g. 1260660"
                    className="w-full border border-secondary-300 rounded px-2 py-1 text-sm font-mono"
                  />
                </label>
                <label className="text-xs text-secondary-600 md:col-span-1">
                  <div>Note (optional)</div>
                  <input
                    type="text" maxLength={512}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="e.g. Q3 contract"
                    className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
                  />
                </label>
              </div>
              <div className="flex justify-end">
                <Button size="sm" onClick={() => void onSave()} disabled={!canSave || saving}>
                  {saving ? "Saving…" : "Save price"}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Price table */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Vendor part #</th>
              <th className="text-left px-3 py-2">Customer part #</th>
              <th className="text-right px-3 py-2">Unit price</th>
              <th className="text-left px-3 py-2">Effective</th>
              <th className="text-left px-3 py-2">Note</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {vendorId == null && customerId == null ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-secondary-500">Pick a vendor or customer above to list prices.</td></tr>
            ) : loading && rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-secondary-500">loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-secondary-500">No prices for the current filter.</td></tr>
            ) : (
              rows.map((r) => (
                <tr
                  key={`${r.vendor_company_id}-${r.customer_company_id}-${r.item_id}-${r.effective_utc ?? ""}`}
                  className="hover:bg-secondary-50 align-top"
                >
                  <td className="px-3 py-2">{r.vendor_name ?? `#${r.vendor_company_id}`}</td>
                  <td className="px-3 py-2">{r.customer_name ?? `#${r.customer_company_id}`}</td>
                  <td className="px-3 py-2">
                    {r.item_name && <div className="text-secondary-700">{r.item_name}</div>}
                    {!r.item_name && <span className="text-secondary-500">#{r.item_id}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono">{r.sku ?? "—"}</td>
                  <td className="px-3 py-2 font-mono">{r.customer_part_no ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {fmtMoney(r.unit_price, r.currency)}
                  </td>
                  <td className="px-3 py-2 text-secondary-700">{fmtUtc(r.effective_utc)}</td>
                  <td className="px-3 py-2 text-secondary-500">{r.note ?? ""}</td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => void onEnd(r)}>
                      End
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(vendorId != null || customerId != null) && total > 0 && (
        <div className="flex items-center justify-between text-xs text-secondary-600">
          <div>
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </div>
          <div className="flex gap-1">
            <Button
              size="sm" variant="outline"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Prev
            </Button>
            <Button
              size="sm" variant="outline"
              disabled={offset + PAGE_SIZE >= total || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {importOpen && (
        <Modal
          open
          onClose={closeImport}
          title="Bulk import prices (CSV)"
          widthClass="max-w-3xl"
        >
          <div className="space-y-3 text-sm">
            <p className="text-xs text-secondary-600">
              Paste CSV content below or pick a file. Required header
              columns: <code>vendor_company_id, customer_company_id,
              item_id, unit_price</code>. Optional:{" "}
              <code>currency, customer_part_no, note</code>. The
              human-readable columns from Export CSV
              (<code>vendor_name, customer_name, sku, item_name</code>)
              are ignored on write. Each row is its own atomic upsert;
              one bad row doesn't block the rest.
            </p>

            <label className="block">
              <span className="text-xs text-secondary-600">Pick a file</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onPickImportFile(f);
                }}
                className="block text-xs mt-1"
              />
            </label>

            <label className="block">
              <span className="text-xs text-secondary-600">…or paste CSV</span>
              <textarea
                rows={10}
                value={importCsv}
                onChange={(e) => setImportCsv(e.target.value)}
                placeholder={
                  "vendor_company_id,customer_company_id,item_id,unit_price,currency,customer_part_no,note\n" +
                  "500,1001,9001,12.50,USD,1260660,Q3 contract\n"
                }
                className="w-full border border-secondary-300 rounded px-2 py-1 text-xs font-mono mt-1"
              />
            </label>

            {importResult && (
              <div
                className={
                  "border rounded p-3 text-xs " +
                  (importResult.errors.length === 0
                    ? "bg-success-50 border-success-200 text-success-800"
                    : "bg-warning-50 border-warning-200 text-warning-900")
                }
              >
                <div className="font-semibold">
                  {importResult.created} of {importResult.total_input_rows}{" "}
                  row{importResult.total_input_rows === 1 ? "" : "s"} created.
                  {importResult.errors.length > 0 && (
                    <> {importResult.errors.length} skipped.</>
                  )}
                </div>
                {importResult.errors.length > 0 && (
                  <ul className="mt-2 list-disc list-inside space-y-0.5">
                    {importResult.errors.slice(0, 25).map((e) => (
                      <li key={e.row_index}>
                        <span className="font-mono">row {e.row_index}:</span>{" "}
                        {e.message}
                      </li>
                    ))}
                    {importResult.errors.length > 25 && (
                      <li className="italic">
                        …{importResult.errors.length - 25} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={closeImport}>
                Close
              </Button>
              <Button
                size="sm" disabled={importing || !importCsv.trim()}
                onClick={() => void onSubmitImport()}
              >
                {importing ? "Importing…" : "Import"}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PricesTab;
